import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  CheckCircle2, 
  Store, 
  UtensilsCrossed, 
  CalendarCheck, 
  Package, 
  Shirt, 
  Trophy, 
  Users, 
  Megaphone,
  Zap,
  Shield,
  BarChart3,
  Clock
} from "lucide-react";

const HomePage = () => {
  const services = [
    { icon: UtensilsCrossed, title: "Ristorazione", description: "Gestione completa per ristoranti, bar e locali" },
    { icon: Store, title: "Retail", description: "Soluzioni per negozi e attività commerciali" },
    { icon: CalendarCheck, title: "Prenotazioni", description: "Sistema integrato di gestione prenotazioni" },
    { icon: Package, title: "Magazzino", description: "Controllo inventario e forniture" },
    { icon: Shirt, title: "Lavanderia", description: "Gestione completa per lavanderie" },
    { icon: Trophy, title: "Sport", description: "Per società sportive dilettantistiche" },
    { icon: Users, title: "HR", description: "Gestione risorse umane integrata" },
    { icon: Megaphone, title: "Promozioni", description: "Campagne e promozioni commerciali" },
  ];

  const features = [
    { icon: Zap, title: "Efficienza Operativa", description: "Automatizza i processi e riduci i tempi di gestione" },
    { icon: Shield, title: "Conformità Fiscale", description: "Invio automatico all'Agenzia delle Entrate" },
    { icon: BarChart3, title: "Controllo Totale", description: "Dashboard in tempo reale per monitorare tutto" },
    { icon: Clock, title: "Risparmio Tempo", description: "Elimina il registratore telematico tradizionale" },
  ];

  const stats = [
    { value: "900+", label: "Punti vendita attivi" },
    { value: "15+", label: "Anni di esperienza" },
    { value: "8", label: "Settori coperti" },
    { value: "24/7", label: "Supporto dedicato" },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white" data-testid="hero-section">
        <div className="section-container section-padding">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Text Content */}
            <div className="space-y-8 animate-slide-left">
              <div>
                <span className="badge mb-4">Piattaforma Gestionale Completa</span>
                <h1 className="heading-1 text-[#1a1f36] mb-6">
                  Il tuo partner digitale per{" "}
                  <span className="text-[#FF6B00]">retail e ristorazione</span>
                </h1>
                <p className="body-large max-w-xl">
                  Semplifica la gestione, aumenta le vendite e mantieni il pieno controllo 
                  del tuo business con un'unica piattaforma integrata.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/soluzioni" className="btn-primary" data-testid="hero-cta-primary">
                  Scopri le Soluzioni
                  <ArrowRight size={18} />
                </Link>
                <Link to="/prenota" className="btn-secondary" data-testid="hero-cta-secondary">
                  Richiedi Demo
                </Link>
              </div>

              <div className="flex flex-wrap gap-6 pt-4">
                {["Nessun registratore telematico", "Conforme normativa", "Setup in 24h"].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-[#4a5068]">
                    <CheckCircle2 size={16} className="text-[#FF6B00]" />
                    {item}
                  </div>
                ))}
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative animate-slide-right">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl shadow-[#1a1f36]/20">
                <img
                  src="/images/hero-pos.jpg"
                  alt="Modern POS system in use"
                  className="w-full h-full object-cover"
                  loading="eager"
                />
              </div>
              {/* Floating stats card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 hidden lg:block border border-[#e2e4ed]">
                <div className="flex items-center gap-4">
                  <div className="icon-container-lg">
                    <BarChart3 size={24} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-[#1a1f36]">+45%</p>
                    <p className="text-sm text-[#7a7f94]">Efficienza operativa</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Navy */}
      <section className="bg-[#1a1f36] py-16" data-testid="stats-section">
        <div className="section-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={stat.label} 
                className={`text-center animate-fade-in stagger-${index + 1}`}
              >
                <p className="text-4xl md:text-5xl font-bold text-[#FF6B00] mb-2">{stat.value}</p>
                <p className="text-[#9ca3af] text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-[#f5f6fa] section-padding" data-testid="features-section">
        <div className="section-container">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="caption mb-4 block">Perché iSiAP</span>
            <h2 className="heading-2 text-[#1a1f36] mb-6">
              Tutto ciò di cui hai bisogno, in un'unica piattaforma
            </h2>
            <p className="body-large">
              Elimina la complessità, riduci i costi e concentrati su ciò che conta: 
              far crescere la tua attività.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={feature.title} 
                className={`feature-card animate-fade-in stagger-${index + 1}`}
                data-testid={`feature-${feature.title.toLowerCase().replace(" ", "-")}`}
              >
                <div className="icon-container mb-6">
                  <feature.icon size={24} />
                </div>
                <h3 className="text-lg font-semibold text-[#1a1f36] mb-2">{feature.title}</h3>
                <p className="text-[#4a5068] text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding" data-testid="services-section">
        <div className="section-container">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div className="max-w-2xl">
              <span className="caption mb-4 block">Soluzioni</span>
              <h2 className="heading-2 text-[#1a1f36] mb-4">
                Diversi contesti, un unico sistema
              </h2>
              <p className="body-large">
                iSiAP si adatta alle esigenze specifiche di ogni settore, 
                offrendo funzionalità dedicate per ogni tipo di attività.
              </p>
            </div>
            <Link 
              to="/soluzioni" 
              className="btn-secondary shrink-0"
              data-testid="services-view-all"
            >
              Vedi Tutte
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div 
                key={service.title}
                className={`service-card card-hover animate-fade-in stagger-${index + 1}`}
                data-testid={`service-${service.title.toLowerCase()}`}
              >
                <div>
                  <div className="number-badge mb-6">
                    {String(index + 1).padStart(2, "0")}
                  </div>
                  <h3 className="text-xl font-semibold text-[#1a1f36] mb-2">{service.title}</h3>
                  <p className="text-[#4a5068] text-sm">{service.description}</p>
                </div>
                <div className="mt-6 pt-6 border-t border-[#e2e4ed]">
                  <service.icon size={32} className="text-[#FF6B00]/30" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value Proposition Section - Navy */}
      <section className="bg-[#1a1f36] section-padding" data-testid="value-prop-section">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div>
                <span className="text-[#FF6B00] text-sm font-semibold uppercase tracking-wider mb-4 block">
                  La Soluzione
                </span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                  Dimentica il registratore telematico tradizionale
                </h2>
                <p className="text-[#9ca3af] text-lg leading-relaxed">
                  iSi integra gestione delle vendite, controllo del magazzino e invio automatico 
                  dei documenti commerciali direttamente al cassetto fiscale tramite le credenziali 
                  dell'Agenzia delle Entrate.
                </p>
              </div>

              <ul className="space-y-4">
                {[
                  "Elimina costi di manutenzione hardware",
                  "Nessuna revisione periodica necessaria",
                  "Aggiornamenti automatici sempre inclusi",
                  "Visione in tempo reale del business"
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-white">
                    <CheckCircle2 size={20} className="text-[#FF6B00] shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>

              <Link 
                to="/contatti" 
                className="inline-flex items-center gap-2 bg-[#FF6B00] text-white px-8 py-4 rounded-lg font-semibold hover:bg-[#E55F00] transition-colors"
                data-testid="value-prop-cta"
              >
                Scopri di più
                <ArrowRight size={18} />
              </Link>
            </div>

            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img
                  src="/images/value-prop.jpg"
                  alt="Team working with digital solutions"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" data-testid="cta-section">
        <div className="section-container">
          <div className="bg-[#f5f6fa] rounded-2xl p-12 md:p-16 text-center border border-[#e2e4ed]">
            <h2 className="heading-2 text-[#1a1f36] mb-6">
              Pronto a trasformare la tua attività?
            </h2>
            <p className="body-large max-w-2xl mx-auto mb-8">
              Scopri come iSiAP può semplificare la gestione del tuo business. 
              Richiedi una demo gratuita e senza impegno.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/prenota" className="btn-primary" data-testid="final-cta-primary">
                Richiedi Demo Gratuita
                <ArrowRight size={18} />
              </Link>
              <a href="tel:0383644311" className="btn-secondary" data-testid="final-cta-phone">
                Chiamaci: 0383 644311
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
